import React, { Component } from 'react';
import {
  Typography, PageHeader, Tag, Upload, Button, Modal, Space, Divider, Alert, Row, Col, Card, Result,
} from 'antd';
import { CheckCircleOutlined, LockOutlined, EditOutlined, ExclamationCircleOutlined, MobileOutlined, GlobalOutlined, GoogleCircleFilled, AppleFilled, PhoneOutlined, MailOutlined } from '@ant-design/icons';
import qs from 'qs';
import firebase from 'firebase/app';
import moment from 'moment';

import StrikeClientModel from '../../../models/StrikeClientModel';
import { ProfileImage, VerifiedClientTag } from '../../../components/client/AccountProfileComponents';
import { EditClientProfileForm, AdminCheckBoxFormItems } from '../../../components/client/EditClientProfileForm';
import ProfileCompletionAlert from '../../../components/client/ProfileCompletionAlert';
import { getCategoryNameWithCompany } from '../../../components/client/ClientCategoryFormItem';
import { ClientCategoryDisplay } from '../../../components/client/ClientCategoryDisplay';
import CenteredSpinner from '../../../components/CenteredSpinner';
import { defaultCurrencyByCountry, countryFlags } from '../../../utils/CurrencyUtils';
import { formatE164ForDisplay } from '../../../utils/phone';
import 'antd/dist/antd.css';
import '../../../App.scss';
import { ClientSpecificPricingView } from '../../../components/admin/ClientSpecificPricingView';
import { ClientImageEditor } from '../../../components/client/UploadableImageEditor';

const { Paragraph, Text } = Typography;

// Country code to name mapping
export const countryByCountryCode = {
  'US' : 'United States',
  'IE' : 'Ireland',
  'GB' : 'United Kingdom',
  'AT' : 'Austria',
  'BE' : 'Belgium',
  'BG' : 'Bulgaria',
  'HR' : 'Croatia',
  'CY' : 'Cyprus',
  'EE' : 'Estonia',
  'ES' : 'Spain',
  'FI' : 'Finland',
  'FR' : 'France',
  'DE' : 'Germany',
  'GR' : 'Greece',
  'IT' : 'Italy',
  'LV' : 'Latvia',
  'LT' : 'Lithuania',
  'LU' : 'Luxembourg',
  'MT' : 'Malta',
  'NL' : 'Netherlands',
  'PT' : 'Portugal',
  'SK' : 'Slovakia',
  'SI' : 'Slovenia',
};

// /client/manage/profile
export default class EditClientProfileScreen extends Component {

  // Properties used by this component:

  constructor(props) {
    super(props);

    const clientId = this.props.match.params.clientId;
    const isAdmin = !!props.isAdmin;

    const parsed = qs.parse(this.props.location.search, { ignoreQueryPrefix: true });
    const mode = (parsed.mode) ? 'editing' : 'viewing';

    this.model = new StrikeClientModel({ viewModel: this, clientId, isAdmin });
    this.isAdmin = isAdmin;

    this.state = {
      user: null,
      client: null,
      clientId: clientId,
      percent: 0,
      loading: true,
      mode,
      windowWidth: typeof window !== 'undefined' ? window.innerWidth : 1024,
      clientLoggedInEmail: null, // Store the client's Firebase auth email (for admin view)
      /** Set when StrikeClientModel cannot load client (missing doc or access denied). */
      clientLoadFailed: false,
      authPhone: null,
      authProviders: [],
    };
  }

  componentDidMount() {
    this.model.waitForClientData();
    this.handleResize = () => {
      this.setState({ windowWidth: window.innerWidth });
    };
    window.addEventListener('resize', this.handleResize);
    // Self-service only: seed login methods from the signed-in user. In ADMIN mode these
    // belong to the CLIENT, not the admin — fetched from the target's Auth record in
    // componentDidUpdate once the client (ownerId) is loaded. Never read currentUser here
    // for admins, or we'd show the admin's own credentials on the client's profile.
    const currentUser = firebase.auth().currentUser;
    if (!this.isAdmin && currentUser) {
      this.setState({
        authPhone: currentUser.phoneNumber || null,
        authProviders: (currentUser.providerData || []).map((p) => ({
          providerId: p.providerId,
          email: p.email || null,
        })),
      });
    }
  }

  fetchClientLoggedInEmail = async () => {
    if (!this.state.client || !this.state.client.ownerId) return;
    
    try {
      const db = firebase.firestore();
      const userDoc = await db.collection('users').doc(this.state.client.ownerId).get();
      if (userDoc.exists) {
        const userData = userDoc.data();
        if (userData.email) {
          this.setState({ clientLoggedInEmail: userData.email });
        }
      }
    } catch (error) {
      console.error('Error fetching client email from users collection:', error);
    }
  }

  componentDidUpdate(prevProps, prevState) {
    // Fetch the CLIENT's email + login methods when client data becomes available in admin mode
    if (this.isAdmin && this.state.client && this.state.client.ownerId &&
        (!prevState.client || prevState.client.ownerId !== this.state.client.ownerId)) {
      this.fetchClientLoggedInEmail();
      this.fetchClientAuthProviders();
    }

    // DELEGATE visiting on the client surface (client loads async, so decided here):
    // the seeded methods are the VIEWER's — shown under the visited client's profile
    // they read as the client's, and the owner's real methods are PRIVATE (delegates
    // cannot read the owner's users doc, by design). Hide the panel entirely;
    // credential management belongs on each person's own account.
    if (!this.isAdmin && this.state.client && this.state.client.ownerId &&
        (!prevState.client || prevState.client.ownerId !== this.state.client.ownerId)) {
      const currentUser = firebase.auth().currentUser;
      if (currentUser && this.state.client.ownerId !== currentUser.uid) {
        this.setState({ authProviders: [], authPhone: null });
      }
    }
  }

  // Admin view: the login methods on this profile belong to the CLIENT, not the logged-in
  // admin. Fetch the target's Firebase Auth providers (+ phone) via the Admin-SDK callable —
  // never read firebase.auth().currentUser here, or we'd show the admin's own credentials.
  fetchClientAuthProviders = async () => {
    if (!this.isAdmin || !this.state.client || !this.state.client.ownerId) return;
    try {
      const result = await firebase.functions().httpsCallable(
        'userService-adminAuthHandler-getClientAuthProviders',
      )({ ownerId: this.state.client.ownerId });
      const { providerData, phoneNumber } = result.data || {};
      this.setState({
        authPhone: phoneNumber || null,
        authProviders: (providerData || []).map((p) => ({
          providerId: p.providerId,
          email: p.email || null,
        })),
      });
    } catch (error) {
      console.warn('[EditClientProfileScreen] getClientAuthProviders failed:', error.message);
    }
  }

  componentWillUnmount() {
    if (this.model) this.model.stop();
    if (this.handleResize) {
      window.removeEventListener('resize', this.handleResize);
    }
  }

  isLoading = () => {
    return this.state.loading || false;
  }

  clientNotFound = () => {
    this.setState({
      client: null,
      loading: false,
      clientLoadFailed: true,
      clientLoggedInEmail: null,
    });
  };

  foundClient = async (client) => {
    this.setState(
      (state) => {
        return {
          client,
          loading: false,
          clientLoadFailed: false,
        }
      }
    );
    // Fetch client's logged in email if admin and client is now available
    if (this.isAdmin && client && client.ownerId) {
      this.fetchClientLoggedInEmail();
    }
  }

  changeMode = (mode) => {
    if (mode === 'viewing') {
      return 'editing'
    } else if (mode === 'editing') {
      return 'saving'
    } else {
      return 'viewing';
    }
  }

  onClickEdit = () => {
    this.setState((state) => {
      return {
        mode: this.changeMode(state.mode)
      }
    }
    );
  }

  doneSaving = () => {
    this.setState((state) => {
      return {
        mode: this.changeMode(state.mode)
      }
    }
    );

    this.model.waitForClientData();
  }

  internalSave = async (data) => {
    await this.model.commit(data);
    this.doneSaving();
  }


  onClickSave = (data) => {
    // console.log(`onClickSave`, data);

    this.setState((state) => {
      return {
        mode: this.changeMode(state.mode),
      }
    }
    );

    this.internalSave(data);
  }

  getButtons = () => {
    if (this.state.mode === 'viewing') {
      return [
        <Button key="1" type="primary" onClick={this.onClickEdit}>
          Edit
        </Button>,
      ]
    } else if (this.state.mode === 'editing') {
      return [];
    } else {
      return [];
    }
  }

  formattedDob = () => {
    if (!this.state.client.dob) return "";
    // Convert moment to Date and use locale-aware formatting with numeric month
    const date = this.state.client.dob.toDate();
    return date.toLocaleDateString(undefined, {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric'
    });
  };

  formattedAgreedTermsAt = () => {
    if (!this.state.client.agreedTermsAt) return "";
    // Convert moment (which is already in local timezone) to Date for locale-aware formatting
    const date = this.state.client.agreedTermsAt.toDate();
    return date.toLocaleDateString(undefined, {
      day: '2-digit',
      month: 'short', // 3-letter month abbreviation
      year: 'numeric'
    });
  };

  formattedMarketingOptInAt = () => {
    if (!this.state.client.marketingOptInAt) return "";
    // Convert moment (which is already in local timezone) to Date for locale-aware formatting
    const date = this.state.client.marketingOptInAt.toDate();
    return date.toLocaleDateString(undefined, {
      day: '2-digit',
      month: 'short', // 3-letter month abbreviation
      year: 'numeric'
    });
  };

  getPreviewName = () => {
    const { displayName } = this.state.client || {};
    if (!displayName || this.state.client.isCompany) return null;
    
    const words = displayName.trim().split(/\s+/).filter(word => word.length > 0);
    if (words.length >= 2) {
      const firstName = words[0];
      const lastName = words[words.length - 1];
      const lastInitial = lastName.match(/[a-zA-Z]/)?.[0]?.toUpperCase() || '';
      return `${firstName} ${lastInitial}.`;
    }
    return null;
  };

  getLoggedInEmail = () => {
    // If admin mode, return the client's email from users collection (or null if not fetched yet)
    if (this.isAdmin) {
      return this.state.clientLoggedInEmail || null;
    }
    // For non-admin, return current user's email
    const currentUser = firebase.auth().currentUser;
    return currentUser ? currentUser.email : null;
  };

  getLoginEmailLabel = () => {
    const authProvider = this.state.client && this.state.client.authProvider ? `${this.state.client.authProvider}`.toLowerCase() : null;
    if (authProvider === 'google') return 'Login with Google:';
    if (authProvider === 'email') return 'Login with Email:';
    if (authProvider === 'apple') return 'Login with Apple:';
    return 'Login email:';
  };

  getPlatformBadge = () => {
    const normalized = this.state.client && this.state.client.platform ? `${this.state.client.platform}`.toLowerCase() : 'web';
    if (normalized === 'ios') {
      return {
        icon: <MobileOutlined style={{ fontSize: '14px', color: '#8c8c8c' }} />,
        label: 'iOS',
      };
    }

    return {
      icon: <GlobalOutlined style={{ fontSize: '14px', color: '#8c8c8c' }} />,
      label: 'Web',
    };
  };

  getProfileErrors = () => {
    const errors = [];
    const { client } = this.state;
    
    if (!client) return errors;
    
    // Check for missing required fields
    // Note: country can be null for older accounts (they're all IE anyway), so we don't require it when viewing
    // But it will be required when editing (handled by EditClientProfileForm validation)
    if (!client.displayName) errors.push('Full name is missing');
    if (!client.dob) errors.push(client.isCompany ? 'Founded on is missing' : 'Date of birth is missing');
    if (!client.phoneNumber) errors.push('Phone number is missing');
    
    // Check for SSN last 4 (US only)
    if (client.country === 'US' && !client.idLast4) {
      errors.push('We need the last 4 digits of your SSN');
    }
    if (client.country === 'US' && client.idLast4 && !/^\d{4}$/.test(client.idLast4)) {
      errors.push('SSN last 4 must be exactly four digits');
    }
    
    // Check for invalid name format (non-company)
    if (!client.isCompany && client.displayName) {
      const words = client.displayName.trim().split(/\s+/).filter(word => word.length > 0);
      const hasLetterPattern = /[a-zA-Z]/;
      if (words.length < 2 || !words.every(word => hasLetterPattern.test(word))) {
        errors.push('We need your first and last name');
      }
    }
    
    // Check for age requirement (non-company)
    if (!client.isCompany && client.dob) {
      const dobMoment = client.dob;
      const today = moment();
      const age = today.diff(dobMoment, 'years', true);
      if (age < 13) {
        errors.push('You must be at least 13 years old');
      }
    }
    
    return errors;
  };

  render() {
    if (!this.state.loading && this.state.clientLoadFailed) {
      return (
        <div style={{ padding: 24, maxWidth: 560, margin: '0 auto' }}>
          <Result
            status="error"
            title="Could not load your account"
            subTitle="We could not find this client record, or you may not have access. It may still be creating—wait a few seconds and refresh—or sign in again."
            extra={[
              <Button key="reload" type="primary" onClick={() => window.location.reload()}>
                Refresh page
              </Button>,
              <Button key="signin" onClick={() => this.props.history.push('/client/login')}>
                Sign in
              </Button>,
            ]}
          />
        </div>
      );
    }

    if (this.state.mode === 'viewing' && !this.state.loading && this.state.client) {
      let agreeTermsString = (this.state.client.agreeTerms) ? "Agreed Terms" : "Need to agree terms";
      agreeTermsString = (this.state.client.agreedTermsAt) ? agreeTermsString + ` on ${this.formattedAgreedTermsAt()}` : agreeTermsString;

      let marketingOptInString = (this.state.client.marketingOptIn) ? "Allowed Strikepay to send important communications" : "Strikepay may not send other communications";
      marketingOptInString = (this.state.client.marketingOptIn && this.state.client.marketingOptInAt) ? marketingOptInString + ` on ${this.formattedMarketingOptInAt()}` : marketingOptInString;

      const businessOrIndividual = (this.state.client.isCompany) ? "Business" : null;
      const defaultCurrency = defaultCurrencyByCountry[this.state.client.country] || 'EUR';
      const previewName = this.getPreviewName();
      const loggedInEmail = this.getLoggedInEmail();
      const loginEmailLabel = this.getLoginEmailLabel();
      const platformBadge = this.getPlatformBadge();
      const profileErrors = this.getProfileErrors();
      const countryFlag = this.state.client.country ? countryFlags[this.state.client.country] : null;
      const isMobile = this.state.windowWidth < 768;
      const securityPath = this.isAdmin
        ? `/admin/clients/${this.state.clientId}/security`
        : `/client/manage/${this.state.clientId}/security`;

      return (
        <div style={{ height: '100%', padding: '0 16px' }}>
          <PageHeader
            ghost={false}
            title={`Your Profile`}
            tags={this.state.mode === 'viewing' ? null : (this.state.mode === 'editing' ? <Tag color="blue">Editing</Tag> : <Tag color="green">Saving...</Tag>)}
            onBack={(() => window.history.back())}
            extra={this.getButtons()}
          />
          <CenteredSpinner loading={this.isLoading()} noLayout={false} />
          
          {/* Complete your profile */}
          {profileErrors.length > 0 && !this.state.client.isCompany && (
            <div style={{ marginBottom: '16px' }}>
              <ProfileCompletionAlert
                title="Complete your profile"
                items={profileErrors}
                footerText='Click "Edit" to update your profile'
                variant="error"
                onClick={this.onClickEdit}
              />
            </div>
          )}

          <Row gutter={[24, 12]} align="bottom" style={{ maxWidth: '900px', marginLeft: 'auto', marginRight: 'auto' }}>
            {/* Left Column: Profile Picture, Name, Category */}
            <Col xs={24} md={10} style={{ textAlign: 'center' }}>
              <Space direction='vertical' size={8} style={{ width: '100%' }}>
                <ProfileImage
                  condition={() => { return !this.isLoading() && this.state.client }}
                  locStrings={this.props.locStrings}
                  client={this.state.client}
                />
                <VerifiedClientTag client={this.state.client} style={{ fontSize: '15px' }} />
                {/* Customer-facing name (preview) */}
                {previewName && (
                  <Text strong style={{ fontSize: '18px' }}>({previewName})</Text>
                )}
                {/* Category with icon */}
                <ClientCategoryDisplay 
                  categoryCode={this.state.client.categoryCode} 
                  isCompany={this.state.client.isCompany}
                />
                {businessOrIndividual && <Text type='success'>{businessOrIndividual}</Text>}
                
                {/* Login methods */}
                {this.state.authProviders.length > 0 && (
                  <Card
                    size="small"
                    title={(
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span>Login methods</span>
                        <Button
                          type="link"
                          size="small"
                          style={{ padding: 0, fontSize: 12 }}
                          onClick={() => this.props.history.push(securityPath)}
                        >
                          Manage →
                        </Button>
                      </div>
                    )}
                    style={{ marginTop: '16px', textAlign: 'left' }}
                  >
                    <Space wrap>
                      {this.state.authProviders.filter((p) => p.providerId === 'google.com').map((p) => (
                        <Tag key="google" color="blue" icon={<GoogleCircleFilled />}>
                          Google{p.email ? ` · ${p.email}` : ''}
                        </Tag>
                      ))}
                      {this.state.authProviders.filter((p) => p.providerId === 'apple.com').map((p) => (
                        <Tag key="apple" color="default" icon={<AppleFilled />}>
                          Apple{p.email ? ` · ${p.email}` : ''}
                        </Tag>
                      ))}
                      {this.state.authPhone && (
                        <Tag key="phone" color="green" icon={<PhoneOutlined />}>
                          Phone · {formatE164ForDisplay(this.state.authPhone)}
                        </Tag>
                      )}
                      {this.state.authProviders.filter((p) => p.providerId === 'password').map((p) => (
                        <Tag key="email" color="orange" icon={<MailOutlined />}>
                          Email{p.email ? ` · ${p.email}` : ''}
                        </Tag>
                      ))}
                    </Space>
                  </Card>
                )}
              </Space>
            </Col>

            {/* Right Column: All other fields */}
            <Col xs={24} md={14}>
              <Row gutter={[16, 12]}>
            {/* Full Name */}
            <Col xs={24}>
              {/* Mobile: label above, Desktop: inline */}
              <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: '8px', flexWrap: 'wrap' }}>
                <Text strong style={{ display: 'block' }}>Full Name:</Text>
                <Text>{this.state.client.displayName || 'Not set'}</Text>
              </div>
            </Col>

            {/* External name */}
            {previewName && (
              <Col xs={24}>
                {/* Mobile: label above, Desktop: inline */}
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: '8px' }}>
                  <Text strong style={{ display: 'block' }}>External Name:</Text>
                  <Text>{previewName}</Text>
                </div>
              </Col>
            )}

            {/* Profile Email */}
            {this.state.client.email && (
              <Col xs={24}>
                {/* Mobile: label above, Desktop: inline */}
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: '8px' }}>
                  <Text strong style={{ display: 'block' }}>Notification email:</Text>
                  <Text>{this.state.client.email}</Text>
                </div>
              </Col>
            )}

            {/* Phone */}
            {this.state.client.phoneNumber && (
              <Col xs={24}>
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: '8px', flexWrap: 'wrap' }}>
                  <Text strong style={{ display: 'block' }}>Phone:</Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Text>{formatE164ForDisplay(this.state.client.phoneNumber)}</Text>
                    {this.state.authPhone && this.state.authPhone === this.state.client.phoneNumber ? (
                      <CheckCircleOutlined style={{ color: '#52c41a' }} title="Verified login method" />
                    ) : (
                      <Button
                        size="small"
                        type="link"
                        style={{ padding: 0, height: 'auto', fontSize: '12px' }}
                        onClick={() => this.props.history.push(securityPath)}
                      >
                        Verify →
                      </Button>
                    )}
                  </div>
                </div>
              </Col>
            )}

            {/* Date of Birth / Founded */}
            {this.state.client.dob && (
              <Col xs={24}>
                {/* Mobile: label above, Desktop: inline */}
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: '8px' }}>
                  <Text strong style={{ display: 'block' }}>{this.state.client.isCompany ? 'Founded:' : 'Date of Birth:'}</Text>
                  <Text>{this.formattedDob()}</Text>
                </div>
              </Col>
            )}

            {/* Work */}
            {this.state.client.employerName && (
              <Col xs={24}>
                {/* Mobile: label above, Desktop: inline */}
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: '8px' }}>
                  <Text strong style={{ display: 'block' }}>Work:</Text>
                  <Text>{this.state.client.employerName}</Text>
                </div>
              </Col>
            )}

            {/* SSN Last 4 */}
            {this.state.client.idLast4 && (
              <Col xs={24}>
                {/* Mobile: label above, Desktop: inline */}
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: '8px' }}>
                  <Text strong style={{ display: 'block' }}>SSN Last 4:</Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    <Text>****</Text>
                    <CheckCircleOutlined style={{ fontSize: '16px', color: '#52c41a' }} />
                  </div>
                </div>
              </Col>
            )}

            {/* Country */}
            {this.state.client.country && (
              <Col xs={24}>
                {/* Mobile: label above, Desktop: inline */}
                <div style={{ display: 'flex', flexDirection: isMobile ? 'column' : 'row', alignItems: isMobile ? 'flex-start' : 'center', gap: '8px' }}>
                  <Text strong style={{ display: 'block' }}>Country:</Text>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {countryFlag && <Text style={{ fontSize: '20px' }}>{countryFlag}</Text>}
                    <Text>{countryByCountryCode[this.state.client.country] || this.state.client.country}</Text>
                  </div>
                </div>
              </Col>
            )}

                {/* Terms */}
                <Col xs={24}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {this.state.client.agreeTerms && (
                      <CheckCircleOutlined style={{ fontSize: '16px', color: '#52c41a' }} />
                    )}
                    <Text type={this.state.client.agreeTerms ? 'success' : 'danger'}>
                      {agreeTermsString}
                    </Text>
                  </div>
                </Col>

                {/* Marketing */}
                <Col xs={24}>
                  <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                    {this.state.client.marketingOptIn && (
                      <CheckCircleOutlined style={{ fontSize: '16px', color: '#52c41a' }} />
                    )}
                    <Text type={this.state.client.marketingOptIn ? 'success' : 'secondary'}>
                      {marketingOptInString}
                    </Text>
                  </div>
                </Col>
              </Row>
            </Col>
          </Row>

          <Divider />

          <div style={{ maxWidth: '600px', margin: '0 auto' }}>
            <AdminCheckBoxFormItems
              isAdmin={this.isAdmin}
              title="Admin Only Settings"
              isViewOnly={true}
              client={{
                ...this.state.client,
                authEmail: this.state.client.authEmail || this.state.clientLoggedInEmail || this.state.client.email || null,
              }}
            />
            { this.isAdmin &&
              <ClientSpecificPricingView pricing={this.state.client.clientSpecificPricing} currency={defaultCurrency} />
            }
          </div>
        </div>
      );
    } else if (!this.state.loading) {
      const { imageUrl } = this.state;
      const avatarUrl = imageUrl ? imageUrl : this.state.client.avatar;

      return (
        <div style={{ height: '100%' }}>
          <PageHeader
            ghost={false}
            title={`Your Profile`}
            tags={this.state.mode === 'viewing' ? null : (this.state.mode === 'editing' ? <Tag color="blue">Editing</Tag> : <Tag color="green">Saving...</Tag>)}
            onBack={(() => window.history.back())}
            extra={this.getButtons()}
          />

          <ClientImageEditor
            clientId={this.state.clientId}
            client={this.state.client}
            currentUrl={avatarUrl}
          />

          <EditClientProfileForm
            client={this.state.client}
            callback={this.onClickSave}
            isAdmin={this.isAdmin}
          />
        </div>
      );
    } else {
      return (
        <div style={{ height: '100%' }}>
          <PageHeader
            ghost={false}
            title={`Your Profile`}
            tags={this.state.mode === 'viewing' ? null : (this.state.mode === 'editing' ? <Tag color="blue">Editing</Tag> : <Tag color="green">Saving...</Tag>)}
            onBack={(() => window.history.back())}
            extra={this.getButtons()}
          />
        </div>
      );
    }
  }
}

